// SPDX-License-Identifier: AGPL-3.0-or-later
import {Logger} from '@app/features/platform/utils/AppLogger';
import {voiceStatsDB} from '@app/features/voice/diagnostics/VoiceStatsDB';
import type {ConnectionQuality, Room} from 'livekit-client';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('VoiceStatsManager');
export interface VoiceStats {
	audioSendBitrate: number;
	audioRecvBitrate: number;
	videoSendBitrate: number;
	videoRecvBitrate: number;
	audioPacketLoss: number;
	videoPacketLoss: number;
	rtt: number;
	jitter: number;
	participantCount: number;
	duration: number;
}
export interface LatencyDataPoint {
	timestamp: number;
	latency: number;
}
export interface PerTrackStats {
	direction: 'send' | 'recv';
	kind: 'audio' | 'video' | 'unknown';
	ssrc?: number;
	rid?: string;
	mid?: string;
	trackIdentifier?: string;
	codec?: string;
	payloadType?: number;
	bitrateKbps: number;
	packetsLost?: number;
	packetsLossPercent?: number;
	jitterMs?: number;
	framesPerSecond?: number;
	frameWidth?: number;
	frameHeight?: number;
	framesEncoded?: number;
	framesDecoded?: number;
	framesDropped?: number;
	freezeCount?: number;
	totalFreezesDurationMs?: number;
	qualityLimitationReason?: string;
}
export interface TransportInfo {
	candidatePairState?: string;
	localCandidateType?: string;
	localProtocol?: string;
	localNetworkType?: string;
	remoteCandidateType?: string;
	remoteProtocol?: string;
	currentRoundTripTimeMs?: number;
	availableOutgoingBitrate?: number;
	availableIncomingBitrate?: number;
	dtlsState?: string;
	iceState?: string;
	selectedCandidatePairChanges?: number;
}
export interface VoiceStatsSample {
	timestamp: number;
	rtt: number;
	jitter: number;
	audioPacketLoss: number;
	videoPacketLoss: number;
	audioSendBitrate: number;
	audioRecvBitrate: number;
	videoSendBitrate: number;
	videoRecvBitrate: number;
}
type RoomWithEngine = Room & {
	engine?: {
		client?: {
			rtt?: number;
		};
		pcManager?: {
			publisher?: RTCPeerConnection;
			subscriber?: RTCPeerConnection;
		};
	};
};
interface RTCStatsReport {
	type: string;
	id: string;
	kind?: string;
	bytesSent?: number;
	bytesReceived?: number;
	packetsLost?: number;
	packetsReceived?: number;
	jitter?: number;
	state?: string;
	currentRoundTripTime?: number;
	availableOutgoingBitrate?: number;
	availableIncomingBitrate?: number;
	codecId?: string;
	mimeType?: string;
	payloadType?: number;
	ssrc?: number;
	rid?: string;
	mid?: string;
	trackIdentifier?: string;
	framesPerSecond?: number;
	frameWidth?: number;
	frameHeight?: number;
	framesEncoded?: number;
	framesDecoded?: number;
	framesDropped?: number;
	freezeCount?: number;
	totalFreezesDuration?: number;
	qualityLimitationReason?: string;
	localCandidateId?: string;
	remoteCandidateId?: string;
	candidateType?: string;
	protocol?: string;
	networkType?: string;
	nominated?: boolean;
	dtlsState?: string;
	iceState?: string;
	selectedCandidatePairChanges?: number;
	selectedCandidatePairId?: string;
}
const LATENCY_UPDATE_INTERVAL_MS = 2000;
const STATS_UPDATE_INTERVAL_MS = 1000;
const MAX_LATENCY_HISTORY = 60;
const MAX_TIME_SERIES_SAMPLES = 120;
const STATS_CLEANUP_INTERVAL_MS = 60000;
const MAX_STATS_AGE_MS = 300000;
async function collectFromPeerConnection(
	pc: RTCPeerConnection,
	direction: 'send' | 'recv',
	now: number,
): Promise<{
	tracks: Array<PerTrackStats>;
	rtt: number;
	transport: TransportInfo | null;
}> {
	const reports = await pc.getStats();
	const reportsById = new Map<string, RTCStatsReport>();
	for (const report of reports.values()) {
		reportsById.set((report as RTCStatsReport).id, report as RTCStatsReport);
	}
	const tracks: Array<PerTrackStats> = [];
	let rtt = 0;
	let activePair: RTCStatsReport | null = null;
	let transportReport: RTCStatsReport | null = null;
	for (const raw of reports.values()) {
		const report = raw as RTCStatsReport;
		if (report.type === 'candidate-pair' && report.state === 'succeeded' && report.nominated !== false) {
			const crt = report.currentRoundTripTime;
			if (crt) rtt = Math.max(rtt, crt * 1000);
			if (
				!activePair ||
				(typeof report.availableOutgoingBitrate === 'number' && !activePair.availableOutgoingBitrate)
			) {
				activePair = report;
			}
			continue;
		}
		if (report.type === 'transport') {
			transportReport = report;
			continue;
		}
		const isOutbound = direction === 'send' && report.type === 'outbound-rtp';
		const isInbound = direction === 'recv' && report.type === 'inbound-rtp';
		if (!isOutbound && !isInbound) continue;
		const id = (direction === 'send' ? 'pub-' : 'sub-') + report.id;
		const currentBytes = isOutbound ? (report.bytesSent ?? 0) : (report.bytesReceived ?? 0);
		let bitrateKbps = 0;
		const previous = await voiceStatsDB.get(id);
		if (previous) {
			const dt = (now - previous.timestamp) / 1000;
			const db = currentBytes - previous.bytes;
			if (dt > 0 && db >= 0) bitrateKbps = (db * 8) / 1000 / dt;
		}
		await voiceStatsDB.set(id, currentBytes, now);
		const codec = report.codecId ? reportsById.get(report.codecId) : undefined;
		let packetsLossPercent: number | undefined;
		if (
			isInbound &&
			typeof report.packetsLost === 'number' &&
			typeof report.packetsReceived === 'number' &&
			report.packetsReceived + report.packetsLost > 0
		) {
			packetsLossPercent = (report.packetsLost / (report.packetsReceived + report.packetsLost)) * 100;
		}
		const kind = report.kind === 'audio' ? 'audio' : report.kind === 'video' ? 'video' : 'unknown';
		tracks.push({
			direction,
			kind,
			ssrc: report.ssrc,
			rid: report.rid,
			mid: report.mid,
			trackIdentifier: report.trackIdentifier,
			codec: codec?.mimeType,
			payloadType: codec?.payloadType,
			bitrateKbps: Math.round(bitrateKbps),
			packetsLost: report.packetsLost,
			packetsLossPercent: packetsLossPercent !== undefined ? Math.round(packetsLossPercent * 10) / 10 : undefined,
			jitterMs: typeof report.jitter === 'number' ? Math.round(report.jitter * 1000 * 10) / 10 : undefined,
			framesPerSecond: report.framesPerSecond,
			frameWidth: report.frameWidth,
			frameHeight: report.frameHeight,
			framesEncoded: report.framesEncoded,
			framesDecoded: report.framesDecoded,
			framesDropped: report.framesDropped,
			freezeCount: report.freezeCount,
			totalFreezesDurationMs:
				typeof report.totalFreezesDuration === 'number' ? Math.round(report.totalFreezesDuration * 1000) : undefined,
			qualityLimitationReason: report.qualityLimitationReason,
		});
	}
	let transport: TransportInfo | null = null;
	if (activePair || transportReport) {
		const local = activePair?.localCandidateId ? reportsById.get(activePair.localCandidateId) : undefined;
		const remote = activePair?.remoteCandidateId ? reportsById.get(activePair.remoteCandidateId) : undefined;
		transport = {
			candidatePairState: activePair?.state,
			localCandidateType: local?.candidateType,
			localProtocol: local?.protocol,
			localNetworkType: local?.networkType,
			remoteCandidateType: remote?.candidateType,
			remoteProtocol: remote?.protocol,
			currentRoundTripTimeMs:
				typeof activePair?.currentRoundTripTime === 'number'
					? Math.round(activePair.currentRoundTripTime * 1000)
					: undefined,
			availableOutgoingBitrate: activePair?.availableOutgoingBitrate,
			availableIncomingBitrate: activePair?.availableIncomingBitrate,
			dtlsState: transportReport?.dtlsState,
			iceState: transportReport?.iceState,
			selectedCandidatePairChanges: transportReport?.selectedCandidatePairChanges,
		};
	}
	return {tracks, rtt, transport};
}
const initialVoiceStats: VoiceStats = {
	audioSendBitrate: 0,
	audioRecvBitrate: 0,
	videoSendBitrate: 0,
	videoRecvBitrate: 0,
	audioPacketLoss: 0,
	videoPacketLoss: 0,
	rtt: 0,
	jitter: 0,
	participantCount: 0,
	duration: 0,
};
export class VoiceStatsManager {
	private room: Room | null = null;
	private latencyIntervalId: ReturnType<typeof setInterval> | null = null;
	private statsIntervalId: ReturnType<typeof setInterval> | null = null;
	private cleanupIntervalId: ReturnType<typeof setInterval> | null = null;
	currentLatency: number | null = null;
	averageLatency: number | null = null;
	latencyHistory: Array<LatencyDataPoint> = [];
	voiceStats: VoiceStats = initialVoiceStats;
	perTrackStats: Array<PerTrackStats> = [];
	statsTimeSeries: Array<VoiceStatsSample> = [];
	publisherTransport: TransportInfo | null = null;
	subscriberTransport: TransportInfo | null = null;
	connectionStartTime: number | null = null;
	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}
	get estimatedLatency(): number | null {
		if (!this.room) return null;
		const localParticipant = this.room.localParticipant;
		if (!localParticipant) return null;
		const quality: ConnectionQuality = localParticipant.connectionQuality;
		switch (quality) {
			case 'excellent':
				return 30;
			case 'good':
				return 60;
			case 'poor':
				return 120;
			default:
				return null;
		}
	}
	get displayLatency(): number | null {
		const measured = this.currentLatency;
		return measured !== null ? measured : this.estimatedLatency;
	}
	get duration(): number {
		return this.connectionStartTime ? Math.floor((Date.now() - this.connectionStartTime) / 1000) : 0;
	}
	setRoom(room: Room | null): void {
		this.room = room;
		if (room) {
			this.connectionStartTime = Date.now();
		} else {
			this.connectionStartTime = null;
		}
	}
	startLatencyTracking(): void {
		this.stopLatencyTracking();
		logger.debug('Starting latency tracking');
		this.latencyIntervalId = setInterval(() => {
			const engineWrap = this.room as RoomWithEngine;
			if (!engineWrap?.engine?.client?.rtt) {
				logger.debug('No RTT available', {
					hasEngine: !!engineWrap?.engine,
					hasClient: !!engineWrap?.engine?.client,
				});
				return;
			}
			const rtt = Math.round(engineWrap.engine.client.rtt);
			logger.debug('RTT measured', {rtt});
			logger.debug('Updating latency state', {rtt});
			const timestamp = Date.now();
			const newPoint: LatencyDataPoint = {timestamp, latency: rtt};
			runInAction(() => {
				this.latencyHistory.push(newPoint);
				if (this.latencyHistory.length > MAX_LATENCY_HISTORY) {
					this.latencyHistory.shift();
				}
				this.currentLatency = rtt;
				this.averageLatency = this.latencyHistory.length
					? Math.round(this.latencyHistory.reduce((a, b) => a + b.latency, 0) / this.latencyHistory.length)
					: null;
			});
		}, LATENCY_UPDATE_INTERVAL_MS);
	}
	stopLatencyTracking(): void {
		if (this.latencyIntervalId !== null) {
			clearInterval(this.latencyIntervalId);
			this.latencyIntervalId = null;
		}
	}
	startStatsTracking(): void {
		this.stopStatsTracking();
		logger.debug('Starting stats tracking');
		this.statsIntervalId = setInterval(async () => {
			if (!this.room) return;
			try {
				const room = this.room as RoomWithEngine;
				const engine = room?.engine;
				if (!engine?.pcManager) return;
				const publisher = engine.pcManager.publisher;
				const subscriber = engine.pcManager.subscriber;
				if (!publisher && !subscriber) return;
				const now = Date.now();
				const tracks: Array<PerTrackStats> = [];
				let rtt = 0;
				let publisherTransport: TransportInfo | null = null;
				let subscriberTransport: TransportInfo | null = null;
				if (publisher) {
					const result = await collectFromPeerConnection(publisher, 'send', now);
					tracks.push(...result.tracks);
					if (result.rtt > rtt) rtt = result.rtt;
					publisherTransport = result.transport;
				}
				if (subscriber) {
					const result = await collectFromPeerConnection(subscriber, 'recv', now);
					tracks.push(...result.tracks);
					if (result.rtt > rtt) rtt = result.rtt;
					subscriberTransport = result.transport;
				}
				if (rtt === 0 && engine.client?.rtt) rtt = engine.client.rtt;
				let audioSendBitrate = 0;
				let audioRecvBitrate = 0;
				let videoSendBitrate = 0;
				let videoRecvBitrate = 0;
				let audioPacketLossSum = 0;
				let videoPacketLossSum = 0;
				let jitterSumMs = 0;
				let audioRecvCount = 0;
				let videoRecvCount = 0;
				for (const track of tracks) {
					if (track.direction === 'send' && track.kind === 'audio') audioSendBitrate += track.bitrateKbps;
					else if (track.direction === 'send' && track.kind === 'video') videoSendBitrate += track.bitrateKbps;
					else if (track.direction === 'recv' && track.kind === 'audio') {
						audioRecvBitrate += track.bitrateKbps;
						audioRecvCount += 1;
						if (typeof track.packetsLossPercent === 'number') audioPacketLossSum += track.packetsLossPercent;
						if (typeof track.jitterMs === 'number') jitterSumMs += track.jitterMs;
					} else if (track.direction === 'recv' && track.kind === 'video') {
						videoRecvBitrate += track.bitrateKbps;
						videoRecvCount += 1;
						if (typeof track.packetsLossPercent === 'number') videoPacketLossSum += track.packetsLossPercent;
					}
				}
				const audioPacketLoss = audioRecvCount > 0 ? audioPacketLossSum / audioRecvCount : 0;
				const videoPacketLoss = videoRecvCount > 0 ? videoPacketLossSum / videoRecvCount : 0;
				const jitter = audioRecvCount > 0 ? jitterSumMs / audioRecvCount : 0;
				const duration = this.connectionStartTime ? Math.floor((Date.now() - this.connectionStartTime) / 1000) : 0;
				const stats: VoiceStats = {
					audioSendBitrate: Math.round(audioSendBitrate),
					audioRecvBitrate: Math.round(audioRecvBitrate),
					videoSendBitrate: Math.round(videoSendBitrate),
					videoRecvBitrate: Math.round(videoRecvBitrate),
					audioPacketLoss: Math.round(audioPacketLoss * 10) / 10,
					videoPacketLoss: Math.round(videoPacketLoss * 10) / 10,
					rtt: Math.round(rtt),
					jitter: Math.round(jitter),
					participantCount: this.room?.numParticipants ?? 0,
					duration,
				};
				const sample: VoiceStatsSample = {
					timestamp: now,
					rtt: stats.rtt,
					jitter: stats.jitter,
					audioPacketLoss: stats.audioPacketLoss,
					videoPacketLoss: stats.videoPacketLoss,
					audioSendBitrate: stats.audioSendBitrate,
					audioRecvBitrate: stats.audioRecvBitrate,
					videoSendBitrate: stats.videoSendBitrate,
					videoRecvBitrate: stats.videoRecvBitrate,
				};
				runInAction(() => {
					this.voiceStats = stats;
					this.perTrackStats = tracks;
					this.publisherTransport = publisherTransport;
					this.subscriberTransport = subscriberTransport;
					this.statsTimeSeries.push(sample);
					if (this.statsTimeSeries.length > MAX_TIME_SERIES_SAMPLES) {
						this.statsTimeSeries.splice(0, this.statsTimeSeries.length - MAX_TIME_SERIES_SAMPLES);
					}
				});
			} catch (error) {
				logger.debug('Error collecting stats', error);
			}
		}, STATS_UPDATE_INTERVAL_MS);
		this.cleanupIntervalId = setInterval(async () => {
			try {
				await voiceStatsDB.clearOldEntries(MAX_STATS_AGE_MS);
			} catch (error) {
				logger.debug('Error cleaning up old entries', error);
			}
		}, STATS_CLEANUP_INTERVAL_MS);
	}
	stopStatsTracking(): void {
		if (this.statsIntervalId !== null) {
			clearInterval(this.statsIntervalId);
			this.statsIntervalId = null;
		}
		if (this.cleanupIntervalId !== null) {
			clearInterval(this.cleanupIntervalId);
			this.cleanupIntervalId = null;
		}
	}
	cleanup(): void {
		logger.debug('Cleaning up');
		this.stopLatencyTracking();
		this.stopStatsTracking();
		this.room = null;
		this.currentLatency = null;
		this.averageLatency = null;
		this.latencyHistory = [];
		this.voiceStats = initialVoiceStats;
		this.perTrackStats = [];
		this.statsTimeSeries = [];
		this.publisherTransport = null;
		this.subscriberTransport = null;
		this.connectionStartTime = null;
	}
	reset(): void {
		this.currentLatency = null;
		this.averageLatency = null;
		this.latencyHistory = [];
		this.voiceStats = initialVoiceStats;
		this.perTrackStats = [];
		this.statsTimeSeries = [];
		this.publisherTransport = null;
		this.subscriberTransport = null;
	}
}
